import { useState, useEffect } from 'react';
import { 
  Shield,
  CheckSquare,
  Square,
  Info,
  FloppyDisk,
  ArrowCounterClockwise,
  Crown,
  User,
  Users as UsersIcon,
  House,
  Truck,
  Globe
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { 
  ROLES, 
  FEATURES, 
  DEFAULT_PERMISSIONS, 
  FEATURE_CATEGORIES,
  RoleKey
} from '../lib/permissions';

// Icons for roles
const ROLE_ICONS: Record<string, Icon> = {
  superadmin: Crown,
  admin: Shield,
  board_member: UsersIcon,
  resident: User,
  subcontractor: Truck,
  external: Globe
};

export default function PermissionsPage() {
  const { permissions: dbPermissions, savePermissions, refreshPermissions, loading: contextLoading } = usePermissions();
  
  // Local state for editing
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [originalPermissions, setOriginalPermissions] = useState<Record<string, string[]>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize with permissions from context
  useEffect(() => {
    if (dbPermissions && !contextLoading) {
      const perms: Record<string, string[]> = {};
      Object.keys(ROLES).forEach(role => {
        perms[role] = [...(dbPermissions[role as RoleKey] || DEFAULT_PERMISSIONS[role as RoleKey] || [])];
      });
      setPermissions(perms);
      setOriginalPermissions(JSON.parse(JSON.stringify(perms)));
    }
  }, [dbPermissions, contextLoading]);

  // Check for changes
  useEffect(() => {
    setHasChanges(JSON.stringify(permissions) !== JSON.stringify(originalPermissions));
  }, [permissions, originalPermissions]);

  // Toggle a permission for a role
  const togglePermission = (role: string, featureId: string): void => {
    if (role === 'superadmin') {
      toast.error('Superadmin har alltid full åtkomst');
      return;
    }

    setPermissions(prev => {
      const rolePerms = [...(prev[role] || [])];
      const index = rolePerms.indexOf(featureId);
      
      if (index > -1) {
        rolePerms.splice(index, 1);
      } else {
        rolePerms.push(featureId);
      }
      
      return { ...prev, [role]: rolePerms };
    });
  };

  // Save permissions to database
  const handleSave = async () => {
    setSaving(true);
    try {
      const success = await savePermissions(permissions);
      if (success) {
        setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
        toast.success('Behörigheter sparade');
        refreshPermissions(); // Reload from DB
      } else {
        toast.error('Kunde inte spara behörigheter');
      }
    } catch (error) {
      toast.error('Kunde inte spara behörigheter');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = (): void => {
    const defaults: Record<string, string[]> = {};
    Object.keys(ROLES).forEach(role => {
      defaults[role] = [...(DEFAULT_PERMISSIONS[role as RoleKey] || [])];
    });
    setPermissions(defaults);
    toast.info('Återställt till standardbehörigheter');
  };

  // Get roles except superadmin for the matrix
  const editableRoles = Object.entries(ROLES)
    .filter(([key]) => key !== 'superadmin')
    .sort((a, b) => b[1].level - a[1].level);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="permissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Behörighetsmatris
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Konfigurera vad varje roll har tillgång till
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleReset}
            disabled={!hasChanges}
            className="gap-2"
          >
            <ArrowCounterClockwise size={16} />
            Återställ
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="gap-2"
          >
            <FloppyDisk size={16} />
            {saving ? 'Sparar...' : 'Spara ändringar'}
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Så fungerar behörigheter</p>
          <p className="mt-1">
            Klicka på en ruta för att ge eller ta bort åtkomst. Superadmin har alltid full åtkomst 
            och kan inte begränsas. Ändringar sparas först när du klickar "Spara ändringar".
          </p>
        </div>
      </div>

      {/* Permissions Matrix */}
      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-3 px-4 font-medium text-slate-700 sticky left-0 bg-slate-50 min-w-[200px]">
                  Funktion
                </th>
                {editableRoles.map(([key, config]) => {
                  const Icon = ROLE_ICONS[key] || User;
                  return (
                    <th key={key} className="text-center py-3 px-3 font-medium min-w-[100px]">
                      <div className="flex flex-col items-center gap-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                          <Icon size={16} weight="bold" />
                        </div>
                        <span className="text-xs">{config.label}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURE_CATEGORIES.map(category => (
                <>
                  {/* Category header */}
                  <tr key={`cat-${category}`} className="bg-slate-100">
                    <td 
                      colSpan={editableRoles.length + 1} 
                      className="py-2 px-4 font-semibold text-slate-700 text-xs uppercase tracking-wider"
                    >
                      {category}
                    </td>
                  </tr>
                  
                  {/* Features in this category */}
                  {Object.entries(FEATURES)
                    .filter(([, feature]) => feature.category === category)
                    .map(([featureId, feature]) => (
                      <tr key={featureId} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4 sticky left-0 bg-white">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help">
                                  <div className="font-medium text-slate-900">
                                    {feature.label}
                                  </div>
                                  {feature.route && (
                                    <div className="text-xs text-slate-400 font-mono">
                                      {feature.route}
                                    </div>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{feature.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        
                        {editableRoles.map(([roleKey]) => {
                          const isChecked = permissions[roleKey]?.includes(featureId);
                          const isDefault = DEFAULT_PERMISSIONS[roleKey as RoleKey]?.includes(featureId);
                          const isModified = isChecked !== isDefault;
                          
                          return (
                            <td key={roleKey} className="text-center py-3 px-3">
                              <button
                                onClick={() => togglePermission(roleKey, featureId)}
                                className={`p-1 rounded transition-all ${
                                  isChecked 
                                    ? 'text-green-600 hover:text-green-700' 
                                    : 'text-slate-300 hover:text-slate-400'
                                } ${isModified ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                                title={isChecked ? 'Klicka för att ta bort' : 'Klicka för att ge åtkomst'}
                              >
                                {isChecked ? (
                                  <CheckSquare size={24} weight="fill" />
                                ) : (
                                  <Square size={24} />
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-sm text-slate-600">
        <div className="flex items-center gap-2">
          <CheckSquare size={20} className="text-green-600" weight="fill" />
          <span>Har åtkomst</span>
        </div>
        <div className="flex items-center gap-2">
          <Square size={20} className="text-slate-300" />
          <span>Ingen åtkomst</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded ring-2 ring-amber-400" />
          <span>Ändrad från standard</span>
        </div>
      </div>

      {/* Role descriptions */}
      <div className="card-flat">
        <h3 className="text-lg font-semibold mb-4">Rollbeskrivningar</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(ROLES).map(([key, config]) => {
            const Icon = ROLE_ICONS[key] || User;
            return (
              <div key={key} className="flex items-start gap-3 p-3 rounded-lg border">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.color}`}>
                  <Icon size={20} weight="bold" />
                </div>
                <div>
                  <div className="font-medium">{config.label}</div>
                  <div className="text-sm text-slate-500">{config.description}</div>
                  <div className="text-xs text-slate-400 mt-1">Nivå {config.level}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
